/**
 * E07-F04-T09 — how a research long-list row is LABELLED, in one place.
 *
 * The bug: rows that had already resolved to a `companies` record showed a
 * technical identifier instead of a company. `raw_name`/`raw_domain` go NULL on
 * resolve (the canonical values move to the relation), so the AI-check screen's
 * fallback printed the row's primary key — literally "row 4711" — and the Review
 * table's website line printed "no website" for companies whose domain we know.
 *
 * The rule now: the SERVER resolves `row.name` / `row.domain`
 * (`@eer/shared/companyDisplay`), the UI only renders them and, when the name could
 * not be determined, says so EXPLICITLY instead of substituting an id. A view must
 * never reconstruct a label from the raw columns again.
 */
import type { JSX } from 'react';
import { isUnresolvedCompanyName, WEBSITE_NOT_FOUND_LABEL } from '@eer/shared';

/** The explicit "we could not determine this name" text the user reads. */
export const UNRESOLVED_NAME_LABEL = 'Name not determined';

/** Screen-reader / aria label for a row (name, or the explicit marker). */
export function companyAriaLabel(name: string | null | undefined): string {
  return isUnresolvedCompanyName(name) ? UNRESOLVED_NAME_LABEL : name!.trim();
}

/**
 * The company name cell: the resolved name, or an explicit "needs clarification"
 * marker — never a row id, never an empty cell.
 */
export function CompanyName({
  name,
  className = 'font-medium text-slate-800',
}: {
  name: string | null | undefined;
  className?: string;
}): JSX.Element {
  if (isUnresolvedCompanyName(name)) {
    return (
      <span
        data-testid="row-name"
        data-name-unresolved="true"
        className={`${className} italic text-amber-600`}
        title="We could not determine this company's name — open Evidence to see what the row was built from."
      >
        {UNRESOLVED_NAME_LABEL}
      </span>
    );
  }
  return (
    <span data-testid="row-name" className={className}>
      {name}
    </span>
  );
}

/** The website line under the name: a link, or the explicit "Website not found" marker. */
export function CompanyDomain({
  domain,
  className = 'text-[11px] text-gray-400',
}: {
  domain: string | null | undefined;
  className?: string;
}): JSX.Element {
  const value = domain?.trim();
  return (
    <span className={className} data-testid="row-domain">
      {value ? (
        <a
          href={`https://${value}`}
          target="_blank"
          rel="noopener"
          className="text-blue-600 hover:underline"
          title="Open website (new tab)"
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </a>
      ) : (
        // E06-F03-T13-S03: "not found", never "no website" — the company has one.
        <span className="italic text-amber-500">{WEBSITE_NOT_FOUND_LABEL}</span>
      )}
    </span>
  );
}
